/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useRef } from 'react';
import translate from 'services/i18n/translate';
import { useSelector } from 'react-redux';
import { Rail } from 'ui/components';
import { Selectors as PlayerSelectors } from 'store/ducks/player';
import { Asset, EntryPoint, Ducks } from '@wit/vtv-x-foundation-lib';
import Constants from 'configs/Constants';
import SideSuggestion from './SideSuggestion';
import { useRouter } from '../../../../../hooks/useRouter';

function Recommendations() {
  const router = useRouter();
  const hasBeenShown = useRef(false);

  const recommendations = useSelector(PlayerSelectors.recommendations);
  const duration = useSelector(PlayerSelectors.currentDuration);
  const progress = useSelector(PlayerSelectors.currentProgress);
  const configs = useSelector(Ducks.Dms.Selectors.getRecommendations);

  const preventForwardLink = useRef(false);

  const [level, setLevel] = React.useState(0);

  const onCardClick = useCallback(
    (assetClicked: Asset, menuItemId?: number) => {
      router.push({ asset: assetClicked, state: { forceAutoPlay: true }, entryPoint: EntryPoint.PLAYER, menuItemId });
    },
    [router]
  );

  const onClose = useCallback((preventLink?: boolean) => {
    setLevel(0);
    if (preventLink) {
      preventForwardLink.current = true;
    }
  }, []);

  const isTimingCorrect = useCallback(() => {
    if (!configs) {
      return false;
    }
    if ((100 * Math.floor(progress)) / Math.floor(duration) >= configs.upNextProgressOndemand) {
      if (Math.floor(duration) - Math.floor(progress) >= configs.upNextMaxTime) {
        return false;
      }
      return true;
    }
    if (Math.floor(duration) - Math.floor(progress) <= configs.upNextMinTime) {
      return true;
    }
    return false;
  }, [configs, duration, progress]);

  useEffect(() => {
    if (!hasBeenShown.current && recommendations.upNext?.assets.length && duration > 0 && isTimingCorrect()) {
      hasBeenShown.current = true;
      setLevel(1);
    } else if (
      !hasBeenShown.current &&
      !recommendations.upNext?.assets.length &&
      duration > 0 &&
      recommendations.somethingSimilar?.assets.length &&
      isTimingCorrect()
    ) {
      hasBeenShown.current = true;
      setLevel(2);
    }
  }, [duration, progress, isTimingCorrect, recommendations]);

  switch (level) {
    default:
    case 0:
      return null;
    case 1:
      return (
        <SideSuggestion
          onCardClick={onCardClick}
          onClose={() => onClose(true)}
          button={
            recommendations.somethingSimilar?.assets.length
              ? { label: translate.upnextRecommendations, handler: () => setLevel(2) }
              : undefined
          }
          asset={recommendations.upNext?.assets.length ? recommendations.upNext.assets[0] : undefined}
          menuItemId={recommendations.upNext?.menuItemId}
        />
      );
    case 2:
      return (
        <div className="up-next" onClick={() => setLevel(0)} id="UpNextRecommendations">
          <div className="assets-wrapper">
            <div className="assets-container">
              {recommendations.somethingSimilar?.assets.length ? (
                <Rail
                  className="upnext-rail"
                  railName={translate.upnextRecommendations1}
                  railId="somethingSimilar"
                  menuItemId={recommendations.somethingSimilar.menuItemId}
                  assets={recommendations.somethingSimilar.assets}
                  maxNumberOfAssets={Constants.RECOMM_MAX_ASSETS_SOMETHING_ELSE}
                  onCardClick={onCardClick}
                />
              ) : null}
              {recommendations.somethingDifferent?.assets.length ? (
                <Rail
                  className="upnext-rail"
                  railName={translate.upnextRecommendations2}
                  railId="somethingDifferent"
                  menuItemId={recommendations.somethingDifferent.menuItemId}
                  assets={recommendations.somethingDifferent.assets}
                  maxNumberOfAssets={Constants.RECOMM_MAX_ASSETS_SOMETHING_ELSE}
                  onCardClick={onCardClick}
                />
              ) : null}
            </div>
          </div>
        </div>
      );
  }
}

export default React.memo(Recommendations);
